import { $byId, type TElementSpecifier, toJQuery } from "@de-xima/xima-common-js-dom";
import {
  isBlank,
  isEmpty,
  notEmpty,
  parseBoolean,
  parseString,
  removeEnd,
  removeStart,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { findWidgetForElement } from "./Util.ts";

const ITEM_SELECTOR_LEGACY =
  "table > tr:not(.ui-rowgroup-header), tbody tr:not(.ui-rowgroup-header), li, .ui-search-item";
const ITEM_SELECTOR = ".ui-search-item";

/**
 * Returns the text to be used for the given search item.
 * @param searchItem Item to search for.
 * @returns Searchable text of the item.
 */
function getItemText(searchItem: JQuery) {
  const $data = searchItem.find(".ui-search-data");
  if ($data.length < 1) {
    return searchItem.text();
  }
  return $data.first().text();
}

function doPlainTextSearch(searchExpression: string, searchItems: JQuery): void {
  const andSplit = searchExpression.split(" ");
  const orSplit = searchExpression.split("|");

  // Apply search terms connected with and.
  for (const and of andSplit) {
    const andTerm = and.trim().toLowerCase();
    for (const element of searchItems) {
      const $element = $(element);
      const text = getItemText($element);
      if (text.toLowerCase().indexOf(andTerm) >= 0) {
        $element.removeClass("data-entry-filtered");
        $element.addClass("data-entry-unfiltered");
      } else {
        $element.addClass("data-entry-filtered");
        $element.removeClass("data-entry-unfiltered");
      }
    }
  }

  // Apply search terms connected with or.
  for (const or of orSplit) {
    const orTerm = or.trim().toLowerCase();
    if (orTerm.length > 0) {
      for (const element of searchItems) {
        const $element = $(element);
        const text = getItemText($element);
        if (text.toLowerCase().indexOf(orTerm) >= 0) {
          $element.removeClass("data-entry-filtered");
          $element.addClass("data-entry-unfiltered");
        }
      }
    }
  }
}

function doRegexpSearch(searchExpression: string, searchItems: JQuery): boolean {
  try {
    const regexp = new RegExp(removeStart(removeEnd(searchExpression, "/"), "/"), "i");
    for (const element of searchItems) {
      const $element = $(element);
      const text = getItemText($element);
      if (regexp.test(text.toLowerCase())) {
        $element.removeClass("data-entry-filtered");
        $element.addClass("data-entry-unfiltered");
      } else {
        $element.addClass("data-entry-filtered");
        $element.removeClass("data-entry-unfiltered");
      }
    }
    return true;
  } catch {
    // Ignore error, most likely user is not done entering the regexp
    return false;
  }
}

// Attach delegate listener for search fields.
export function initSearch(): void {
  const searchbarSelector = "[data-filter-for-field],[data-filter-function],.searchbar-input";
  const dataTableFilterSelector = "input.ui-column-filter, textarea.ui-column-filter";

  $(document).off("keyup keydown blur focusout searchbar:filter", searchbarSelector, dataFilterForFieldKeyUP);
  $(document).on("keydown focusout", searchbarSelector, dataFilterForFieldKeyUP);
  $(document).on("searchbar:filter", searchbarSelector, dataFilterForFieldKeyUP);

  $(document).off("keydown blur focusout", dataTableFilterSelector);
  $(document).on("keydown focusout", dataTableFilterSelector, function (event) {
    if (event.type === "keydown" && event.key !== "Enter") {
      return;
    }

    if (event.type === "keydown") {
      event.preventDefault();
    }

    const widget = findWidgetForElement($(this).closest(".ui-datatable"), PrimeFaces.widget.DataTable);
    widget?.filter();
  });
}

/**
 * Called when the search icon of the list finder was clicked. Expands or
 * collapses the search field.
 * @param button Button that was clicked.
 * @returns Whether the search was formerly expanded.
 */
export function onListFinderClicked(button: JQuery): boolean {
  const $button = $(button);
  const panel = $button.siblings(".ui-panel");

  if (panel.length === 0) {
    console.error("no search panel found");
    return false;
  }

  const panelWidget = findWidgetForElement(panel, PrimeFaces.widget.Panel);
  if (panelWidget === undefined) {
    return false;
  }

  const searchbarInput = panel.find(".searchbar-input");
  const formerlyExpanded = panelWidget.content.is(":visible");

  panelWidget.toggle();
  searchbarInput.val("").trigger("keyup");

  if (!formerlyExpanded) {
    searchbarInput.trigger("focus");
  }

  return formerlyExpanded;
}

/**
 * Called when the search button was clicked (that hides or shows the search panel). Adds
 * a class to the context container for CSS styling when the search panel is open.
 * @param button Button that was clicked.
 * @param formerlyExpanded Whether the search panel was formerly expanded.
 */
export function selectListToggleFinder(button: TElementSpecifier<HTMLElement>, formerlyExpanded: boolean): void {
  const element = toJQuery(button).closest(".context-sidebar");
  element.toggleClass("finder-open", !formerlyExpanded);
}

/**
 * Performs the actual search. The this context of this function must be set to the input element with the search
 * expression. It may have some data attributes set to customize the search:
 *
 * - `filterForField` ID of the container with the items.
 * - `filterFunction` If set, delegates the actual search to this function. It must show/hide the items appropriately.
 * - `filterForFieldUp` Selector for finding the container with the items (see below).
 * - `filterForFieldDown` Selector for finding the container with the items (see below).
 *
 * (That is, to specify a value for `filterForField`, add the HTML attribute `data-filter-for-field="list"`)
 *
 * The container with the search items is searched for as follows: (1) If `filterForFieldUp` or `filterForFieldDown` is
 * set, starting with the base element, look for closest parent matching the selector `filterForFieldUp`, then look
 * for the first child matching the selector `filterForFieldDown`. (2) Otherwise, look for an element with the ID given
 * by `filterForField`.
 *
 * The items to be filtered are searched as follows. Start with the container, then search for all containing elements
 * with at least one of the following properties:
 *
 * - Have the class `ui-search-item` set
 * - Have the tag name `LI`
 * - Match the selector `table > tr:not(.ui-rowgroup-header)`
 * - Match the selector `tbody tr:not(.ui-rowgroup-header)`
 *
 * In case the HTML data attribute `data-explicit-only` is set to `true`, only the first condition is considered, ie.
 * only elements that have the class `ui-search-item`.
 *
 * Finally, all matching items with the class `ui-search-exclude` are excluded.
 *
 * For each item, the text to be searched for is either (1) the text content of the first child element with the class
 * `ui-search-data` or (2) the text content of the item itself, if no such child exists.
 *
 * The search expressions is split into search terms on whitespaces, the item text must contain all of the individual
 * search terms.
 *
 * When `data-parent-container-selector` is set on the search bar, hides all element in the container that match the
 * selector when all items of that element are filtered and not being displayed currently.
 */
export function dataFilterForFieldKeyUP(this: HTMLElement | JQuery, event: JQuery.TriggeredEvent): void {
  if (event.type === "keydown" && event.key !== "Enter") {
    return;
  }

  if (
    event.type !== "keydown" &&
    event.type !== "blur" &&
    event.type !== "focusout" &&
    event.type !== "searchbar:filter"
  ) {
    return;
  }

  if (event.type === "keydown") {
    event.preventDefault();
  }

  const $searchbar = $(this);

  // Custom filter function
  const filterFunction = parseString($searchbar.data("filterFunction"));
  if (notEmpty(filterFunction)) {
    PrimeFaces.csp.eval(filterFunction);
  } else {
    doFilter($searchbar);
  }
}

/**
 * Performs a client-side filtering. Search `dataFilterForFieldKeyUP` for more details.
 * @param $searchbar Input element with the search expression.
 * @returns All items that were considered for the search, some of those may now be hidden.
 */
export function doFilter($searchbar: JQuery): JQuery {
  const parentContainerSelector = parseString($searchbar.data("parentContainerSelector"));
  const selectorUp = parseString($searchbar.data("filterForFieldUp"));
  const selectorDown = parseString($searchbar.data("filterForFieldDown"));
  const explicitOnly = parseBoolean($searchbar.data("explicitOnly"));

  let container: JQuery<HTMLElement> | JQuery<Document>;
  if (notEmpty(selectorDown) || notEmpty(selectorUp)) {
    const $up = notEmpty(selectorUp) ? $searchbar.closest(selectorUp) : $(document);
    container = notEmpty(selectorDown) ? $up.find(selectorDown) : $up;
  }
  // Search by ID
  else {
    container = $byId(parseString($searchbar.data("filterForField")));
    if (container.length === 0) {
      // If not found, the element may be in a naming container that contributes a prefix to the ID.
      const id = parseString($searchbar.data("filterForField"));
      container = $(`[id$="\\:${id}"]`);
    }
  }

  const searchExpression = String($searchbar.val() ?? "").trim();
  const searchItems = $(explicitOnly ? ITEM_SELECTOR : ITEM_SELECTOR_LEGACY, container).not(".ui-search-exclude");

  // If no search term was entered, show all items.
  if (isEmpty(searchExpression)) {
    searchItems.removeClass("data-entry-filtered");
    searchItems.addClass("data-entry-unfiltered");
    const menuElements2 = $('ul[role="menu"]', container);
    menuElements2.removeClass("DispBlock");
    menuElements2.parent('li[role="menuitem"]').removeClass("filtered-menuitem");
  } else {
    container.parents(".nano.has-scrollbar").first().scrollTop(0);
    const menuElements = $('ul[role="menu"]', container);
    menuElements.addClass("DispBlock");
    menuElements.parent('li[role="menuitem"]').addClass("filtered-menuitem");

    let regexpSearch = false;

    // Try regexp if asked
    if (searchExpression.startsWith("/")) {
      regexpSearch = doRegexpSearch(searchExpression, searchItems);
    }

    // If not successful or not asked for, do normal search
    if (!regexpSearch) {
      doPlainTextSearch(searchExpression, searchItems);
    }
  }

  // When no element matches, hide the parent container
  if (notEmpty(parentContainerSelector)) {
    for (const parent of container.find(parentContainerSelector)) {
      const $parent = $(parent);
      if ($parent.find(".data-entry-unfiltered").length === 0) {
        $parent.addClass("DispNone");
      } else {
        $parent.removeClass("DispNone");
      }
    }
  }

  for (const dataTable of searchItems.closest(".ui-datatable")) {
    const widget = findWidgetForElement(dataTable, PrimeFaces.widget.DataTable);
    if (widget?.bodyTable !== undefined) {
      if (isEmpty(searchExpression)) {
        widget.bodyTable.removeClass("ui-datatable-body-filtered");
      } else {
        widget.bodyTable.addClass("ui-datatable-body-filtered");
      }
    }
  }

  return searchItems;
}

export function filterScopeChooser(element: JQuery, event: JQuery.TriggeredEvent): boolean {
  if (event.keyCode === 13) {
    return false;
  }
  const query = element.val()?.toString().toUpperCase();
  const overlay = element.closest(".ui-overlaypanel-content");
  const links = overlay.find("a.scope-choose-item-link");
  if (query === undefined || isBlank(query)) {
    links.show();
  } else {
    links.each((_idx, el) => {
      const $el = $(el);
      const scopeName = $el.find(".scope-name").text();
      if (scopeName.toUpperCase().includes(query)) {
        $el.show();
      } else {
        $el.hide();
      }
    });
  }
  return true;
}

export function search(
  input: HTMLInputElement,
  searchableClass: string = "searchable-content",
  searchableContainerClass: string = "searchable-content-container",
  searchClearBtnClass: string = "search-clear-btn",
): void {
  const query = input.value.toLowerCase();
  const clearButton = findSiblingWithClass(input, searchClearBtnClass);
  const searchables = document.querySelectorAll(`.${searchableClass}`);
  if (clearButton) {
    clearButton.style.display = query ? "block" : "none";
  }

  for (const searchable of searchables) {
    if (!(searchable instanceof HTMLElement)) {
      return;
    }
    const container = searchable.closest(`.${searchableContainerClass}`);
    const textContent = searchable.textContent;
    const content = textContent == null ? "" : textContent.toLowerCase();

    if (content.includes(query)) {
      if (container instanceof HTMLElement) {
        container.style.display = "";
      }
      highlightText(searchable, query);
    } else {
      if (container instanceof HTMLElement) {
        container.style.display = "none";
      }
      clearHighlight(searchable);
    }
  }
}

export function clearSearch(
  triggerElement: HTMLElement,
  searchableClass: string = "searchable-content",
  searchableContainerClass: string = "searchable-content-container",
  searchInputClass: string = "search-input",
  searchClearBtnClass: string = "search-clear-btn",
): void {
  const searchables = document.querySelectorAll(`.${searchableClass}`);
  for (const searchable of searchables) {
    if (!(searchable instanceof HTMLElement)) {
      return;
    }
    clearHighlight(searchable);
  }

  const searchableContainer = document.querySelectorAll(`.${searchableContainerClass}`);
  for (const container of searchableContainer) {
    if (!(container instanceof HTMLElement)) {
      return;
    }
    container.style.display = "";
  }

  const searchInput = findSearchInput(triggerElement, searchInputClass);
  if (searchInput) {
    searchInput.value = "";
  }

  if (triggerElement.classList.contains(searchClearBtnClass)) {
    triggerElement.style.display = "none";
  }
}

function findSearchInput(triggerElement: HTMLElement, searchInputClass: string): HTMLInputElement | null {
  const input = findSiblingWithClass(triggerElement, searchInputClass);
  if (input instanceof HTMLInputElement) {
    return input as HTMLInputElement;
  }
  return null;
}

function findSiblingWithClass(el: HTMLElement, clazz: string): HTMLElement | null {
  if (!el) {
    return null;
  }
  const parent = el.parentElement;
  if (!parent) {
    return null;
  }

  const siblings = Array.from(parent.children).filter((child) => child !== el && child.classList.contains(clazz));

  const sibling = siblings.length > 0 ? siblings[0] : null;
  if (sibling instanceof HTMLElement) {
    return sibling as HTMLElement;
  }
  return null;
}

function highlightText(element: HTMLElement, query: string) {
  if (!element.dataset.originalText) {
    element.dataset.originalText = element.innerHTML;
  }
  clearHighlight(element);
  if (!query) {
    return;
  }

  const nodes = Array.from(element.childNodes);
  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue;
      if (!text) {
        continue;
      }

      const regex = new RegExp(`(${query})`, "gi");
      if (regex.test(text)) {
        const fragments = text.split(regex); // Split text by the query
        const fragmentNodes = fragments.map((fragment) => {
          if (regex.test(fragment)) {
            const span = document.createElement("span");
            span.className = "search-highlight";
            span.textContent = fragment;
            return span;
          } else {
            return document.createTextNode(fragment);
          }
        });

        const parent = node.parentNode;
        if (parent) {
          for (const fragmentNode of fragmentNodes) {
            parent.insertBefore(fragmentNode, node);
          }
          parent.removeChild(node); // Remove the original text node
        }
      }
    } else {
      if (node instanceof HTMLElement) {
        // Recursively process child elements
        highlightText(node, query);
      }
    }
  }
}

function clearHighlight(element: HTMLElement) {
  if (element.dataset.originalText) {
    element.innerHTML = element.dataset.originalText; // Restore original content
  }
}
